<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="sizeClass"
      :style="{
        '--temp-color': tempColorRaw,
        '--pin-active-color': tempColorRaw,
        borderColor: isActive ? tempColorRaw : '',
      }"
      :title="device.deviceName">
      <!-- The ring carries the setpoint within the device's own range (P13);
           inside it the drawn thermometer over the reading. -->
      <ControlStatusRing :value="temperatureFillPct">
        <ControlIcon name="thermometer" :size="pinIconSize" :style="{ color: 'var(--temp-color)' }" />
        <span class="thermo-label">
          {{ targetTemperature !== null ? targetTemperature : '--' }}<span class="thermo-unit">°</span>
        </span>
      </ControlStatusRing>
      <div class="device-icon-pin__glow" :style="climateGlowStyle"></div>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <!-- Card face: the climate unit's own gauge, the target and the reading, and
         the setpoint slider at the largest size. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="heatingOutput !== null && heatingOutput > 0"
      :accent="tempColorRaw"
      class="thermostat-card-face"
      @click="emit('open-control-modal')"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <!-- The sheet's own gauge, not a dial: the same track, gradient and 5°
             tick grid, with the target big and the room's own reading under it. -->
        <div class="arc-gauge-container climate-card-face__gauge">
          <svg
            :viewBox="`${-SVG_PAD} ${-SVG_PAD} ${SVG_SIZE + SVG_PAD * 2} ${SVG_SIZE + SVG_PAD * 2}`"
            class="arc-gauge-svg">
            <path
              :d="arcBgPath"
              fill="none"
              stroke="currentColor"
              class="text-gray-200 dark:text-gray-700"
              :stroke-width="ARC_STROKE"
              stroke-linecap="round" />
            <defs>
              <linearGradient :id="`${arcGradientId}-card`" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#3b82f6" />
                <stop offset="40%" stop-color="#f59e0b" />
                <stop offset="100%" stop-color="#ef4444" />
              </linearGradient>
            </defs>
            <path
              :d="arcBgPath"
              fill="none"
              :stroke="`url(#${arcGradientId}-card)`"
              :stroke-width="ARC_STROKE"
              stroke-linecap="round"
              :stroke-dasharray="arcTotalLen"
              :stroke-dashoffset="arcDashOffset" />
            <line
              v-for="tick in arcTicks"
              :key="tick.key"
              :x1="tick.x1"
              :y1="tick.y1"
              :x2="tick.x2"
              :y2="tick.y2"
              class="arc-tick" />
          </svg>

          <div class="climate-hero-center">
            <div class="climate-hero-value">
              {{ targetTemperature !== null ? targetTemperature.toFixed(1) : '--'
              }}<span class="climate-hero-unit">°</span>
            </div>
            <div class="climate-hero-caption">
              {{
                currentTemperature !== null
                  ? `${currentTemperature.toFixed(1)}° ${tt('room', 'prostor')}`
                  : tt('room', 'prostor')
              }}
            </div>
            <div v-if="heatingOutput !== null" class="thermostat-card-face__heating">
              <span class="material-symbols-outlined">local_fire_department</span>
              {{ Math.round(heatingOutput) }}%
            </div>
          </div>
        </div>
      </template>

      <template v-if="cardSize === 'max'" #control>
        <div class="thermostat-button-controls thermostat-card-face__controls">
          <div
            @click.stop="adjustTemperature(-tempStep)"
            :class="{ 'thermostat-temp-button--disabled': controlling }"
            class="thermostat-temp-button thermostat-temp-button--down"
            :title="tt('Decrease temperature', 'Znižaj temperaturo')"
            :aria-label="tt('Decrease temperature', 'Znižaj temperaturo')">
            <span class="material-symbols-outlined">remove</span>
          </div>
          <div
            @click.stop="adjustTemperature(tempStep)"
            :class="{ 'thermostat-temp-button--disabled': controlling }"
            class="thermostat-temp-button thermostat-temp-button--up"
            :title="tt('Increase temperature', 'Zvišaj temperaturo')"
            :aria-label="tt('Increase temperature', 'Zvišaj temperaturo')">
            <span class="material-symbols-outlined">add</span>
          </div>
        </div>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <!-- Expanded: colSpan >= 2 — icon + name + current temp + setpoint controls -->
    <template v-if="(colSpan ?? 1) >= 2">
      <div
        class="w-full h-full flex flex-col justify-between p-2 rounded-xl select-none"
        :class="isActive ? 'bg-orange-400/15 border border-orange-400/30' : 'bg-white/5 border border-white/10'">
        <!-- Top row: icon + name + current temp -->
        <div class="flex items-center gap-1.5 min-w-0 cursor-pointer" @click="emit('open-control-modal')">
          <span class="material-symbols-outlined text-3xl" :class="isActive ? 'text-orange-400' : 'text-gray-400'">{{
            device.icon || 'thermostat'
          }}</span>
          <span class="text-base font-semibold text-white truncate flex-1">{{ displayName }}</span>
          <span v-if="currentTemperature !== null" class="text-sm text-gray-400 shrink-0"
            >{{ currentTemperature.toFixed(1) }}°</span
          >
        </div>
        <!-- Bottom: setpoint controls -->
        <div class="flex items-center justify-between">
          <button
            @click.stop="adjustTemperature(-tempStep)"
            :disabled="controlling"
            class="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-500/20 text-blue-400 active:scale-90 disabled:opacity-40">
            <span class="material-symbols-outlined text-2xl">remove</span>
          </button>
          <span class="text-2xl font-bold text-orange-400"
            >{{ targetTemperature !== null ? targetTemperature.toFixed(1) : '--' }}°</span
          >
          <button
            @click.stop="adjustTemperature(tempStep)"
            :disabled="controlling"
            class="w-12 h-12 flex items-center justify-center rounded-xl bg-red-500/20 text-red-400 active:scale-90 disabled:opacity-40">
            <span class="material-symbols-outlined text-2xl">add</span>
          </button>
        </div>
      </div>
    </template>
    <!-- Minimal: 1-wide -->
    <template v-else>
      <DeviceGridCell
        :icon="device.icon || 'thermostat'"
        :name="device.deviceName"
        :is-on="isActive"
        active-color="orange-400"
        @toggle="emit('open-control-modal')"
        @long-press="emit('open-control-modal')" />
    </template>
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <!-- The body of the sheet: the host owns the head and the panel, the body
         owns the hero and the rows (no head, no outer padding — the host's body
         container carries it). The climate's own colour (its temperature scale)
         rides down while the device is active; otherwise the host's accent
         stands. -->
    <div class="climate-sheet" :style="isActive ? { '--sheet-accent': tempColorRaw } : {}">
      <!-- HERO — the calm arc: the track plus the active-portion gradient (the
           widget's own drag maths, its 7–35 range and gradient stops, kept), a
           faint 5° tick grid with no numerals, and the current reading big in
           the middle. -->
      <div class="climate-hero">
        <div
          class="arc-gauge-container"
          ref="arcContainer"
          @mousedown="arcMouseDown"
          @touchstart.prevent="arcTouchStart">
          <svg
            :viewBox="`${-SVG_PAD} ${-SVG_PAD} ${SVG_SIZE + SVG_PAD * 2} ${SVG_SIZE + SVG_PAD * 2}`"
            class="arc-gauge-svg">
            <!-- Background arc (track) -->
            <path
              :d="arcBgPath"
              fill="none"
              stroke="currentColor"
              class="text-gray-200 dark:text-gray-700"
              :stroke-width="ARC_STROKE"
              stroke-linecap="round" />
            <!-- Gradient definition -->
            <defs>
              <linearGradient :id="arcGradientId" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#3b82f6" />
                <stop offset="40%" stop-color="#f59e0b" />
                <stop offset="100%" stop-color="#ef4444" />
              </linearGradient>
            </defs>
            <!-- Active arc (filled portion) -->
            <path
              :d="arcBgPath"
              fill="none"
              :stroke="`url(#${arcGradientId})`"
              :stroke-width="ARC_STROKE"
              stroke-linecap="round"
              :stroke-dasharray="arcTotalLen"
              :stroke-dashoffset="arcDashOffset"
              class="transition-[stroke-dashoffset] duration-200"
              :class="{ '!duration-0': isDragging }" />
            <!-- The faint 5° tick grid (the old numeric labels are gone) -->
            <line
              v-for="tick in arcTicks"
              :key="tick.key"
              :x1="tick.x1"
              :y1="tick.y1"
              :x2="tick.x2"
              :y2="tick.y2"
              class="arc-tick" />
          </svg>

          <!-- Center: the current reading, big -->
          <div class="climate-hero-center">
            <div class="climate-hero-value">
              {{ targetTemperature !== null ? targetTemperature.toFixed(1) : '--'
              }}<span class="climate-hero-unit">°C</span>
            </div>
            <div class="climate-hero-caption">
              {{
                currentTemperature !== null
                  ? `${currentTemperature.toFixed(1)}° ${tt('room', 'prostor')}`
                  : tt('room', 'prostor')
              }}
            </div>
          </div>
        </div>
      </div>

      <!-- TARGET — the stepper row on the widget's own setpoint write path
           (`effective_target` / `target_temperature` / `setpoint`). -->
      <ControlStepper :value-text="targetStepperText" :disabled="controlling" @step="stepTarget" />

      <!-- TILES — only the readings this device reports on the wire: Heating
           (`heating_output` / `valve` / `valve_output` / `heating`) and, where
           the device has one, the humidity reading (`humidity`). -->
      <div v-if="heatingOutput !== null || hasHumidity" class="climate-tiles">
        <ControlValueTile
          v-if="heatingOutput !== null"
          icon="flame"
          :label="tt('Heating', 'Gretje')"
          :value="String(heatingOutput)"
          unit="%" />
        <ControlValueTile
          v-if="hasHumidity"
          icon="drop"
          :label="tt('Humidity', 'Vlažnost')"
          :value="humidityValue !== null ? String(humidityValue) : '--'"
          unit="%" />
      </div>

      <!-- ── Operation Mode (single enum) — the sheet's chips ─────────────── -->
      <div v-if="hasOperationMode" class="climate-chips">
        <ControlChip
          v-for="opt in operationModeOptions"
          :key="opt.value"
          :active="currentOperationMode === opt.value"
          :disabled="controlling"
          @click="setOperationMode(opt.value)">
          {{ modeLabel(opt) }}
        </ControlChip>
      </div>

      <!-- ── Binary operation modes ──────────────────────────────────────── -->
      <div v-if="hasBinaryModes" class="climate-chips">
        <ControlChip
          v-for="bm in binaryModeList"
          :key="bm.key"
          :active="binaryModeValue(bm.key)"
          :disabled="controlling"
          @click="toggleBinaryMode(bm.key)">
          {{ modeLabel(bm) }}
        </ControlChip>
      </div>

      <!-- ── Controller Mode ─────────────────────────────────────────────── -->
      <div v-if="hasControllerMode" class="climate-chips">
        <ControlChip
          v-for="opt in controllerModeOptions"
          :key="opt.value"
          :active="currentControllerMode === opt.value"
          :disabled="controlling"
          @click="setControllerMode(opt.value)">
          {{ modeLabel(opt) }}
        </ControlChip>
      </div>

      <!-- ── Heat / Cool — the sheet's two chips ─────────────────────────── -->
      <div v-if="hasHeatCool" class="climate-chips">
        <ControlChip :active="heatCoolValue === true" :disabled="controlling" @click="setHeatCool(true)">
          <ControlIcon name="flame" size="1.0625em" />
          {{ tt('Heat', 'Gretje') }}
        </ControlChip>
        <ControlChip :active="heatCoolValue === false" :disabled="controlling" @click="setHeatCool(false)">
          <ControlIcon name="snowflake" size="1.0625em" />
          {{ tt('Cool', 'Hlajenje') }}
        </ControlChip>
      </div>

      <!-- ── On/Off toggle ───────────────────────────────────────────────── -->
      <ControlToggleRow
        v-if="hasOnOff"
        :model-value="onOffValue"
        icon="power"
        :label="tt('Power', 'Napajanje')"
        :disabled="controlling"
        @update:model-value="setOnOff" />

      <!-- ── Fan speed — percent: the sheet's slider; steps: the chips ───── -->
      <div v-if="hasFanSpeed" class="climate-row">
        <div class="climate-row-head">
          <span class="climate-row-key">
            <ControlIcon name="fan" size="0.9375rem" />
            {{ tt('Fan Speed', 'Hitrost ventilatorja') }}
          </span>
          <span class="climate-row-value">{{ fanIsPercent ? `${fanSpeedValue}%` : fanStepLabel }}</span>
        </div>
        <ControlSlider
          v-if="fanIsPercent"
          :model-value="fanSpeedValue"
          :disabled="controlling"
          :title="tt('Fan Speed', 'Hitrost ventilatorja')"
          :aria-label="tt('Fan Speed', 'Hitrost ventilatorja')"
          @change="handleSetFanStep" />
        <div v-else class="climate-chips">
          <ControlChip
            v-for="opt in fanStepOptions"
            :key="opt.value"
            :active="fanSpeedValue === opt.value"
            :disabled="controlling"
            @click="handleSetFanStep(opt.value)">
            {{ opt.label }}
          </ControlChip>
        </div>
      </div>

      <!-- ── Swing toggles ───────────────────────────────────────────────── -->
      <ControlToggleRow
        v-if="hasSwing"
        :model-value="swingValue"
        icon="wind"
        :label="tt('Swing', 'Nihanje')"
        :disabled="controlling"
        @update:model-value="setSwing" />
      <ControlToggleRow
        v-if="hasSwingH"
        :model-value="swingHValue"
        icon="wind"
        :label="tt('H-Swing', 'Vodoravno nihanje')"
        :disabled="controlling"
        @update:model-value="setSwingH" />

      <!-- Value history logs — kept mounted as-is (the internals are not this
           card's; only the container takes the sheet's divider). The heading's
           glyph is the drawn set's `activity` (P11 sweep, card t_bd3aea71). -->
      <div v-if="loggableValues.length" class="climate-history">
        <p
          class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <ControlIcon name="activity" size="0.875rem" />
          {{ tt('History', 'Zgodovina') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <ValueLogButton
            v-for="v in loggableValues"
            :key="v.id"
            :device-value="v"
            :device="{ id: device.id, deviceName: device.deviceName }" />
        </div>
      </div>

      <!-- META -->
      <div v-if="lastUpdated" class="climate-meta">
        <span class="climate-meta-left">
          <ControlIcon name="clock" size="0.8125rem" />
          {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
        </span>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import type { Bilingual } from '../../config/bilingual';
import { formatTime } from '../../utils/formatDate';
import { ref, computed, watch, onMounted } from 'vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import DeviceCardShell from './DeviceCardShell.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlSlider from '../device-sheet/ControlSlider.vue';
import ControlStepper from '../device-sheet/ControlStepper.vue';
import ControlToggleRow from '../device-sheet/ControlToggleRow.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import { useDeviceValueOptionsStore } from '../../stores/deviceValueOptions';
import ValueLogButton from './ValueLogButton.vue';
import { tt } from '../../utils/consoleText';
import type { Device, DeviceValueWithReferences } from '@/types';
import { readField } from './deviceWidgetTypes';
import type { EnumOptionLike } from './deviceWidgetTypes';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  colSpan?: number;
  rowSpan?: number;
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const isDragging = ref(false);
let tempDebounceTimer: number | null = null;
const fetchedTargetOptions = ref<{ minValue: number | null; maxValue: number | null; step: number | null } | null>(
  null,
);
const fetchedOpModeOptions = ref<Array<{ value: number; label: string }> | null>(null);
const fetchedCtrlModeOptions = ref<Array<{ value: number; label: string }> | null>(null);
const fetchedFanStepOptions = ref<Array<{ value: number; label: string }> | null>(null);
const arcContainer = ref<HTMLElement | null>(null);

const { currentValues, initializeValues, updateValue } = useDeviceValues(props.device.id);
const { controlling, setProperty, findValueByKeys } = useDeviceControl();
const deviceValueOptionsStore = useDeviceValueOptionsStore();

watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { immediate: true },
);

// ── Arc gauge constants ──────────────────────────────────────────────────
const SVG_SIZE = 360;
const SVG_PAD = 40;
const ARC_CX = SVG_SIZE / 2;
const ARC_CY = SVG_SIZE / 2;
const ARC_R = 143;
/** The calm arc's weight (the sketch's stroke-to-radius ratio) — presentation only. */
const ARC_STROKE = 12;
const ARC_START_DEG = 135;
const ARC_END_DEG = 405; // 135 + 270
const ARC_SPAN_DEG = 270;
const arcGradientId = `arc-gradient-${props.device.id}`;

const targetConfigValue = computed<DeviceValueWithReferences | null>(() => {
  for (const key of ['target_temperature', 'setpoint', 'effective_target']) {
    const value = props.device.deviceValues?.find((deviceValue) => deviceValue.valueKey === key);
    if (value) return value;
  }
  return null;
});

const targetValueMeta = computed(() => targetConfigValue.value);

function toFiniteNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function getMetaNumber(source: unknown, keys: string[], fallback: number) {
  for (const key of keys) {
    const numeric = toFiniteNumber(readField(source, key));
    if (numeric !== null) return numeric;
  }
  return fallback;
}

function getTargetMetaNumber(keys: string[], fallback: number) {
  for (const source of [fetchedTargetOptions.value, targetValueMeta.value]) {
    if (!source) continue;
    const numeric = getMetaNumber(source, keys, Number.NaN);
    if (Number.isFinite(numeric)) return numeric;
  }
  return fallback;
}

function getStepPrecision(step: number) {
  const stepText = String(step);
  const decimals = stepText.split('.')[1];
  return decimals?.length ?? 0;
}

function roundToStep(value: number, step: number) {
  return Number((Math.round(value / step) * step).toFixed(getStepPrecision(step)));
}

// Temperature range from device value metadata, with fallbacks
const TEMP_MIN = computed(() => {
  return getTargetMetaNumber(['minValue', 'min_value', 'min'], 7);
});
const TEMP_MAX = computed(() => {
  return getTargetMetaNumber(['maxValue', 'max_value', 'max'], 35);
});
const tempStep = computed(() => {
  const step = getTargetMetaNumber(['step', 'deltaValue', 'delta_value'], 0.5);
  return step > 0 ? step : 0.5;
});

// ── Arc geometry helpers ─────────────────────────────────────────────────
function degToRad(d: number) {
  return (d * Math.PI) / 180;
}
function polarToXY(angleDeg: number, r: number) {
  const rad = degToRad(angleDeg);
  return { x: ARC_CX + r * Math.cos(rad), y: ARC_CY + r * Math.sin(rad) };
}

const arcBgPath = computed(() => {
  const start = polarToXY(ARC_START_DEG, ARC_R);
  const end = polarToXY(ARC_END_DEG, ARC_R);
  return `M ${start.x} ${start.y} A ${ARC_R} ${ARC_R} 0 1 1 ${end.x} ${end.y}`;
});

const arcTotalLen = computed(() => (ARC_SPAN_DEG / 360) * 2 * Math.PI * ARC_R);

const arcDashOffset = computed(() => {
  const t = targetTemperature.value ?? TEMP_MIN.value;
  const pct = Math.max(0, Math.min(1, (t - TEMP_MIN.value) / (TEMP_MAX.value - TEMP_MIN.value)));
  return arcTotalLen.value * (1 - pct);
});

/** The faint 5° tick grid — no numerals; values at 5° steps inside the range. */
const arcTicks = computed(() => {
  const ticks: Array<{ key: string; x1: number; y1: number; x2: number; y2: number }> = [];
  const min = TEMP_MIN.value;
  const max = TEMP_MAX.value;
  const span = max - min;
  if (span <= 0) return ticks;

  const first = Math.ceil(min / 5) * 5;
  for (let value = first; value <= max + 0.001; value += 5) {
    const pct = Math.max(0, Math.min(1, (value - min) / span));
    const angle = ARC_START_DEG + pct * ARC_SPAN_DEG;
    const inner = polarToXY(angle, ARC_R - ARC_STROKE / 2 - 9);
    const outer = polarToXY(angle, ARC_R - ARC_STROKE / 2 - 3);
    ticks.push({ key: `t${value}`, x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y });
  }

  return ticks;
});

// ── Arc drag logic ───────────────────────────────────────────────────────
function angleFromEvent(clientX: number, clientY: number): number | null {
  if (!arcContainer.value) return null;
  const svg = arcContainer.value.querySelector('svg');
  if (!svg) return null;
  const svgRect = svg.getBoundingClientRect();
  const viewBoxSize = SVG_SIZE + SVG_PAD * 2;
  const scaleX = viewBoxSize / svgRect.width;
  const scaleY = viewBoxSize / svgRect.height;
  const x = -SVG_PAD + (clientX - svgRect.left) * scaleX - ARC_CX;
  const y = -SVG_PAD + (clientY - svgRect.top) * scaleY - ARC_CY;
  let angle = Math.atan2(y, x) * (180 / Math.PI);
  if (angle < 0) angle += 360;
  return angle;
}

function angleToTemp(rawAngle: number): number {
  let a = rawAngle - ARC_START_DEG;
  if (a < -45) a += 360;
  const pct = Math.max(0, Math.min(1, a / ARC_SPAN_DEG));
  const step = tempStep.value;
  const rawTemp = TEMP_MIN.value + pct * (TEMP_MAX.value - TEMP_MIN.value);
  return Math.max(TEMP_MIN.value, Math.min(TEMP_MAX.value, Math.round(rawTemp / step) * step));
}

function arcMouseDown(e: MouseEvent) {
  if (controlling.value) return;
  isDragging.value = true;
  const angle = angleFromEvent(e.clientX, e.clientY);
  if (angle !== null) setTemperatureFromArc(angleToTemp(angle));

  function onMove(ev: MouseEvent) {
    const a = angleFromEvent(ev.clientX, ev.clientY);
    if (a !== null) setTemperatureFromArc(angleToTemp(a));
  }
  function onUp() {
    isDragging.value = false;
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  }
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
}

function arcTouchStart(e: TouchEvent) {
  if (controlling.value) return;
  isDragging.value = true;
  const touch = e.touches[0];
  const angle = angleFromEvent(touch.clientX, touch.clientY);
  if (angle !== null) setTemperatureFromArc(angleToTemp(angle));

  function onMove(ev: TouchEvent) {
    const t = ev.touches[0];
    const a = angleFromEvent(t.clientX, t.clientY);
    if (a !== null) setTemperatureFromArc(angleToTemp(a));
  }
  function onEnd() {
    isDragging.value = false;
    document.removeEventListener('touchmove', onMove);
    document.removeEventListener('touchend', onEnd);
  }
  document.addEventListener('touchmove', onMove);
  document.addEventListener('touchend', onEnd);
}

function setTemperatureFromArc(temp: number) {
  // Determine propKey BEFORE optimistic update
  const hasEffective = 'effective_target' in currentValues.value;
  const valueKey = hasEffective ? 'effective_target' : 'target_temperature';
  const propKey = hasEffective ? 'effective_target' : 'targetTemperature';
  updateValue(valueKey, temp);
  if (tempDebounceTimer) clearTimeout(tempDebounceTimer);
  tempDebounceTimer = window.setTimeout(() => {
    setProperty(props.device, propKey, temp);
  }, 200);
}

// ── Temperature ──────────────────────────────────────────────────────────
const currentTemperature = computed<number | null>(() => {
  const key = ['current_temperature', 'temperature'].find((k) => k in currentValues.value);
  if (key) return toFiniteNumber(currentValues.value[key]);
  return toFiniteNumber(findValueByKeys(props.device, ['current_temperature', 'temperature'])?.currentValue);
});

const targetTemperature = computed<number | null>(() => {
  // Prefer effective_target (handler), then target_temperature, then setpoint
  const key = ['effective_target', 'target_temperature', 'setpoint'].find((k) => k in currentValues.value);
  if (key) return toFiniteNumber(currentValues.value[key]);
  return toFiniteNumber(
    findValueByKeys(props.device, ['effective_target', 'target_temperature', 'setpoint'])?.currentValue,
  );
});

const heatingOutput = computed<number | null>(() => {
  const key = ['heating_output', 'valve', 'valve_output', 'heating'].find((k) => k in currentValues.value);
  if (key) return toFiniteNumber(currentValues.value[key]);
  return toFiniteNumber(findValueByKeys(props.device, ['heating_output', 'valve', 'valve_output'])?.currentValue);
});

const tempSliderPct = computed(() => {
  const t = targetTemperature.value ?? TEMP_MIN.value;
  return Math.max(0, Math.min(100, ((t - TEMP_MIN.value) / (TEMP_MAX.value - TEMP_MIN.value)) * 100));
});

const temperatureFillPct = computed(() => tempSliderPct.value);

const tempColorRaw = computed(() => {
  // Mirror the 3-stop arc gradient: blue(0%) → amber(40%) → red(100%)
  // #3b82f6 → #f59e0b → #ef4444
  const p = tempSliderPct.value / 100;
  let r: number, g: number, b: number;
  if (p <= 0.4) {
    const t = p / 0.4;
    r = Math.round(59 + (245 - 59) * t);
    g = Math.round(130 + (158 - 130) * t);
    b = Math.round(246 + (11 - 246) * t);
  } else {
    const t = (p - 0.4) / 0.6;
    r = Math.round(245 + (239 - 245) * t);
    g = Math.round(158 + (68 - 158) * t);
    b = Math.round(11 + (68 - 11) * t);
  }
  return `rgb(${r},${g},${b})`;
});

const climateGlowStyle = computed(() => {
  const m = tempColorRaw.value.match(/rgb\((\d+),(\d+),(\d+)\)/);
  if (!m) return {};
  return {
    background: `radial-gradient(circle, rgba(${m[1]},${m[2]},${m[3]},0.55) 0%, rgba(${m[1]},${m[2]},${m[3]},0.22) 45%, transparent 72%)`,
  };
});

const isActive = computed(() => {
  if (heatingOutput.value !== null && heatingOutput.value > 0) return true;
  if ('on_off' in currentValues.value) return Boolean(currentValues.value['on_off']);
  if ('is_active' in currentValues.value) return Boolean(currentValues.value['is_active']);
  if ('active_state' in currentValues.value) return Boolean(currentValues.value['active_state']);
  return targetTemperature.value !== null;
});

// ── Operation Mode ───────────────────────────────────────────────────────
const hasOperationMode = computed(
  () =>
    'operation_mode' in currentValues.value || props.device.deviceValues?.some((v) => v.valueKey === 'operation_mode'),
);

const currentOperationMode = computed(() => {
  const v = currentValues.value['operation_mode'];
  return v !== undefined && v !== null ? Number(v) : null;
});

const FALLBACK_OP_MODES: Array<{ value: number; label: Bilingual }> = [
  { value: 0, label: { en: 'Auto', sl: 'Samodejno' } },
  { value: 1, label: { en: 'Comfort', sl: 'Udobje' } },
  { value: 2, label: { en: 'Standby', sl: 'Pripravljenost' } },
  { value: 3, label: { en: 'Economy', sl: 'Varčno' } },
  { value: 4, label: { en: 'Protection', sl: 'Zaščita' } },
];

/**
 * A mode option's own word: an `enum_options` entry the hub sent carries a
 * plain string (data, shown as it came), the build's own fallback a pair.
 */
function modeLabel(option: { label: string | Bilingual }): string {
  return typeof option.label === 'string' ? option.label : tt(option.label.en, option.label.sl);
}

const operationModeOptions = computed(() => fetchedOpModeOptions.value ?? FALLBACK_OP_MODES);

// ── Controller Mode ──────────────────────────────────────────────────────
const hasControllerMode = computed(
  () =>
    'controller_mode' in currentValues.value ||
    props.device.deviceValues?.some((v) => v.valueKey === 'controller_mode'),
);

const currentControllerMode = computed(() => {
  const v = currentValues.value['controller_mode'];
  return v !== undefined && v !== null ? Number(v) : null;
});

const FALLBACK_CTRL_MODES: Array<{ value: number; label: Bilingual }> = [
  { value: 0, label: { en: 'Auto', sl: 'Samodejno' } },
  { value: 1, label: { en: 'Heat', sl: 'Gretje' } },
  { value: 2, label: { en: 'Cool', sl: 'Hlajenje' } },
  { value: 3, label: { en: 'Off', sl: 'Izklopljeno' } },
  { value: 6, label: { en: 'Fan Only', sl: 'Samo ventilator' } },
  { value: 8, label: { en: 'Dry', sl: 'Sušenje' } },
];

// ── Binary operation modes (individual booleans) ─────────────────────────
const binaryModeList: Array<{ key: string; label: Bilingual }> = [
  { key: 'operation_mode_comfort', label: { en: 'Comfort', sl: 'Udobje' } },
  { key: 'operation_mode_standby', label: { en: 'Standby', sl: 'Pripravljenost' } },
  { key: 'operation_mode_night', label: { en: 'Economy', sl: 'Varčno' } },
  { key: 'operation_mode_frost_protection', label: { en: 'Protection', sl: 'Zaščita' } },
];

const hasBinaryModes = computed(() =>
  binaryModeList.some(
    (bm) => bm.key in currentValues.value || props.device.deviceValues?.some((v) => v.valueKey === bm.key),
  ),
);

function binaryModeValue(key: string): boolean {
  const v = currentValues.value[key];
  return v === true || v === 1;
}

async function toggleBinaryMode(key: string) {
  await setProperty(props.device, key, !binaryModeValue(key));
}

const controllerModeOptions = computed(() => fetchedCtrlModeOptions.value ?? FALLBACK_CTRL_MODES);

// ── Heat / Cool ──────────────────────────────────────────────────────────
const hasHeatCool = computed(
  () => 'heat_cool' in currentValues.value || props.device.deviceValues?.some((v) => v.valueKey === 'heat_cool'),
);
const heatCoolValue = computed(() => {
  const v = currentValues.value['heat_cool'];
  return v !== undefined && v !== null ? Boolean(v) : null;
});

// ── On/Off ───────────────────────────────────────────────────────────────
const hasOnOff = computed(
  () => 'on_off' in currentValues.value || props.device.deviceValues?.some((v) => v.valueKey === 'on_off'),
);
const onOffValue = computed(() => {
  const v = currentValues.value['on_off'];
  return v === true || v === 1;
});

// ── Fan Speed ────────────────────────────────────────────────────────────
const hasFanSpeed = computed(
  () => 'fan_speed' in currentValues.value || props.device.deviceValues?.some((v) => v.valueKey === 'fan_speed'),
);
const fanSpeedValue = computed(() => {
  const v = currentValues.value['fan_speed'];
  return typeof v === 'number' ? Math.round(v) : 0;
});

/** `enum_options` off a device-value row, or `[]` when the row carries none. */
function enumOptionsOf(row: DeviceValueWithReferences | undefined): EnumOptionLike[] {
  const raw = readField(row, 'enum_options');
  if (!Array.isArray(raw)) return [];
  return raw.map((option) => ({ value: readField(option, 'value'), label: readField(option, 'label') }));
}

const fanIsPercent = computed(() => {
  const dv = props.device.deviceValues?.find((v) => v.valueKey === 'fan_speed');
  if (enumOptionsOf(dv).length) return false;
  if (dv?.unit === '%') return true;
  if (fetchedFanStepOptions.value?.length) return false;
  const max = readField(dv, 'max_value') ?? readField(dv, 'max');
  if (max !== undefined && Number(max) <= 10) return false;
  return true;
});

const fanStepOptions = computed<Array<{ value: number; label: string }>>(() => {
  if (fetchedFanStepOptions.value?.length) return fetchedFanStepOptions.value;
  const dv = props.device.deviceValues?.find((v) => v.valueKey === 'fan_speed');
  const options = enumOptionsOf(dv);
  if (options.length) {
    return options.map((o) => ({ value: Number(o.value), label: String(o.label) }));
  }
  return [
    { value: 0, label: tt('Off', 'Izklopljeno') },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
  ];
});

const fanStepLabel = computed(
  () => fanStepOptions.value.find((o) => o.value === fanSpeedValue.value)?.label ?? String(fanSpeedValue.value),
);

// ── Swing ────────────────────────────────────────────────────────────────
const hasSwing = computed(
  () => 'swing' in currentValues.value || props.device.deviceValues?.some((v) => v.valueKey === 'swing'),
);
const swingValue = computed(() => Boolean(currentValues.value['swing']));
const hasSwingH = computed(
  () =>
    'swing_horizontal' in currentValues.value ||
    props.device.deviceValues?.some((v) => v.valueKey === 'swing_horizontal'),
);
const swingHValue = computed(() => Boolean(currentValues.value['swing_horizontal']));

// ── Humidity ─────────────────────────────────────────────────────────────
const hasHumidity = computed(
  () => 'humidity' in currentValues.value || props.device.deviceValues?.some((v) => v.valueKey === 'humidity'),
);
const humidityValue = computed<number | null>(() => {
  const v = currentValues.value['humidity'];
  return typeof v === 'number' ? Math.round(v) : null;
});

// ── Last Updated ─────────────────────────────────────────────────────────
const loggableValues = computed(() =>
  (props.device.deviceValues ?? []).filter((v) => v.loggingType && v.loggingType !== 'None'),
);

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

// ── Actions ──────────────────────────────────────────────────────────────
async function adjustTemperature(delta: number) {
  const current = targetTemperature.value ?? 21;
  const newTemp = Math.max(TEMP_MIN.value, Math.min(TEMP_MAX.value, roundToStep(current + delta, tempStep.value)));
  // Determine propKey BEFORE optimistic update to avoid the check being affected by updateValue
  const hasEffective = 'effective_target' in currentValues.value;
  const valueKey = hasEffective ? 'effective_target' : 'target_temperature';
  const propKey = hasEffective ? 'effective_target' : 'targetTemperature';
  updateValue(valueKey, newTemp);
  await setProperty(props.device, propKey, newTemp);
}

// ── Sheet rows ───────────────────────────────────────────────────────────
/** The stepper's readout — the sketch's `21.8 °C target`. */
const targetStepperText = computed(() =>
  targetTemperature.value !== null
    ? `${targetTemperature.value.toFixed(1)} °C ${tt('target', 'ciljna')}`
    : `-- °C ${tt('target', 'ciljna')}`,
);

/** One step of the sheet's stepper — the widget's own ± write path, kept. */
function stepTarget(delta: 1 | -1) {
  void adjustTemperature(delta * tempStep.value);
}

async function setOperationMode(value: number) {
  await setProperty(props.device, 'operation_mode', value);
}

async function setControllerMode(value: number) {
  await setProperty(props.device, 'controller_mode', value);
}

async function setHeatCool(heat: boolean) {
  await setProperty(props.device, 'heat_cool', heat);
}

/** The Power toggle row — the widget's own `on_off` write, on the row's value. */
async function setOnOff(value: boolean) {
  await setProperty(props.device, 'on_off', value);
}

/** The fan row's writes — step chips and the percent slider both land here. */
async function handleSetFanStep(value: number) {
  await setProperty(props.device, 'fan_speed', value);
}

async function setSwing(value: boolean) {
  await setProperty(props.device, 'swing', value);
}

async function setSwingH(value: boolean) {
  await setProperty(props.device, 'swing_horizontal', value);
}

// ── Display ──────────────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** Icon mode (P13): the drawn thermometer's size inside the pin's ring. */
const pinIconSize = computed(() => {
  if (props.fluid) return '20px';
  switch (props.size) {
    case 'lg':
      return '22px';
    case 'sm':
      return '12px';
    default:
      return '16px';
  }
});

async function fetchTargetTemperatureOptions() {
  const targetValue = targetConfigValue.value;
  if (!targetValue?.id) {
    fetchedTargetOptions.value = null;
    return;
  }

  try {
    const options = await deviceValueOptionsStore.fetchOptions(targetValue.id);
    fetchedTargetOptions.value = {
      minValue: toFiniteNumber(options?.min_value ?? options?.minValue),
      maxValue: toFiniteNumber(options?.max_value ?? options?.maxValue),
      step: toFiniteNumber(options?.step ?? options?.delta_value ?? options?.deltaValue),
    };
  } catch {
    fetchedTargetOptions.value = null;
  }
}

watch(
  [() => targetConfigValue.value?.id, () => props.device.settings],
  () => {
    void fetchTargetTemperatureOptions();
  },
  { immediate: true, deep: true },
);

onMounted(async () => {
  // Fetch enum options for operation_mode
  const opModeDv = props.device.deviceValues?.find((v) => v.valueKey === 'operation_mode');
  if (opModeDv?.id) {
    try {
      const opts = await deviceValueOptionsStore.fetchOptions(opModeDv.id);
      if (opts?.enum_options?.length) {
        fetchedOpModeOptions.value = opts.enum_options.map((o) => ({ value: Number(o.value), label: String(o.label) }));
      }
    } catch {
      /* fallback to defaults */
    }
  }

  // Fetch enum options for controller_mode
  const ctrlModeDv = props.device.deviceValues?.find((v) => v.valueKey === 'controller_mode');
  if (ctrlModeDv?.id) {
    try {
      const opts = await deviceValueOptionsStore.fetchOptions(ctrlModeDv.id);
      if (opts?.enum_options?.length) {
        fetchedCtrlModeOptions.value = opts.enum_options.map((o) => ({
          value: Number(o.value),
          label: String(o.label),
        }));
      }
    } catch {
      /* fallback to defaults */
    }
  }

  // Fetch enum options for fan_speed (step mode)
  const fanDv = props.device.deviceValues?.find((v) => v.valueKey === 'fan_speed');
  if (fanDv?.id) {
    try {
      const opts = await deviceValueOptionsStore.fetchOptions(fanDv.id);
      if (opts?.enum_options?.length) {
        fetchedFanStepOptions.value = opts.enum_options.map((o) => ({
          value: Number(o.value),
          label: String(o.label),
        }));
      }
    } catch {
      /* ignore */
    }
  }
});
</script>

<style scoped>
/* ── Icon mode: the ring's clear circle holds the thermometer over the °C ── */
.thermo-label {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  font-weight: 700;
  font-size: 11px;
  color: var(--temp-color, rgba(255, 255, 255, 0.95));
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  line-height: 1;
  pointer-events: none;
}
.thermo-unit {
  font-size: 0.65em;
}

/* ── Button mode ── */
.thermostat-colored .device-icon span {
  color: var(--temp-color) !important;
}
.thermostat-colored {
  border-color: var(--temp-color) !important;
}

/* Stacked layout: head row on top, controls and the name caption at the bottom */
.thermostat-layout {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  margin-top: 0;
  min-height: 0;
}

.thermostat-button-head {
  display: flex;
  align-items: center;
  gap: 0.55em;
  min-width: 0;
}

/* The tiles' own circle: the shared opaque, page-coloured fill, so the
   temp-coloured ring reads against one flat surface instead of the translucent
   tile behind it. The fill itself stays neutral. */
.thermostat-icon-circle {
  width: 2.35em;
  height: 2.35em;
  margin-bottom: 0;
  border-radius: 50%;
  background: var(--icon-circle-fill);
  border: 1px solid rgba(148, 163, 184, 0.35);
}

/* Device photo fills the icon circle in the head row */
.thermostat-icon-circle :deep(.device-thumb) {
  width: 100%;
  height: 100%;
}

.thermostat-button-setpoint {
  min-width: 0;
  font-size: var(--tile-status-size);
  font-weight: 700;
  color: rgb(var(--gray-900));
}

/* Name above the value, beside the circle */
.thermostat-button-text {
  display: flex;
  flex-direction: column;
  gap: 0.1em;
  min-width: 0;
}

/* Smaller than the setpoint, and kept to a single line (ellipsis when too long) */
.thermostat-layout .device-name {
  font-size: 0.62em;
  min-width: 0;
}

.status-slider-vertical {
  position: absolute;
  right: 0.75em;
  top: 50%;
  transform: translateY(-50%);
  height: 60%;
  width: 0.4em;
  flex-shrink: 0;
}
.status-slider-track {
  width: 100%;
  height: 100%;
  background: rgba(156, 163, 175, 0.3);
  border-radius: 2px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.status-slider-fill {
  width: 100%;
  transition: height 0.3s ease-out;
  border-radius: 2px;
}
.status-slider-fill--temp {
  background: linear-gradient(180deg, rgb(239, 68, 68), rgb(59, 130, 246));
}

.dark .thermostat-button-setpoint {
  color: #fff;
}

.thermostat-button-setpoint span {
  font-size: 0.85em;
}

/* The room's own reading rides beside the setpoint — quieter and smaller, so
   the setpoint stays the tile's one number */
.thermostat-button-setpoint .thermostat-button-current {
  margin-left: 0.4em;
  font-size: 0.68em;
  font-weight: 600;
  color: rgb(107 114 128);
}

.dark .thermostat-button-setpoint .thermostat-button-current {
  color: rgb(156 163 175);
}

.thermostat-button-controls {
  display: flex;
  align-items: center;
  gap: 0.35em;
  margin-top: auto;
  padding-top: 0.45em;
}

.thermostat-temp-button {
  width: 1.45em;
  height: 1.45em;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease,
    transform 0.15s ease;
}

.thermostat-temp-button--down {
  background: rgb(59 130 246 / 0.15);
  color: #3b82f6;
}

.thermostat-temp-button--up {
  background: rgb(239 68 68 / 0.15);
  color: #ef4444;
}

.dark .thermostat-temp-button--down {
  color: #60a5fa;
}

.dark .thermostat-temp-button--up {
  color: #f87171;
}

.thermostat-temp-button--down:hover:not(:disabled) {
  background: rgb(59 130 246 / 0.28);
}

.thermostat-temp-button--up:hover:not(:disabled) {
  background: rgb(239 68 68 / 0.28);
}

.thermostat-temp-button--disabled {
  opacity: 0.45;
  pointer-events: none;
}

.thermostat-temp-button .material-symbols-outlined {
  font-size: 1rem;
}

.thermostat-temp-slider {
  position: relative;
  flex: 1 1 auto;
  height: 0.65em;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.18);
  border: 1px solid rgba(148, 163, 184, 0.2);
  cursor: pointer;
  touch-action: none;
}

.thermostat-temp-slider__fill {
  position: absolute;
  inset: 0;
  /* Cold → hot scale, matching the arc gauge gradient */
  background: linear-gradient(90deg, #3b82f6 0%, #f59e0b 40%, #ef4444 100%);
  clip-path: inset(0 calc(100% - var(--temp-fill-pct, 0%)) 0 0);
}

/*
 * The climate sheet in the control-sheet language: the calm arc hero (the
 * track + the active-portion gradient, a faint 5° tick grid, the current
 * reading big in the middle), the target stepper row, the reading tiles, the
 * mode / fan chips and the sheet's rows. The body root sets `--sheet-accent`
 * to the climate's own colour while it is active; every primitive reads it.
 */
.climate-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
  min-width: 0;
}

.climate-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 0.25rem;
}

/* ── Arc gauge ── */
.arc-gauge-container {
  position: relative;
  width: 300px;
  height: 300px;
  cursor: pointer;
  touch-action: none;
  user-select: none;
}
.arc-gauge-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.climate-hero-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.climate-hero-value {
  font-size: 2.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--gray-900));
}

.dark .climate-hero-value {
  color: #fff;
}

.climate-hero-unit {
  margin-left: 0.15em;
  font-size: 1.0625rem;
  color: rgb(var(--gray-500));
}

.dark .climate-hero-unit {
  color: rgb(var(--gray-400));
}

.climate-hero-caption {
  margin-top: 0.375rem;
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .climate-hero-caption {
  color: rgb(var(--gray-400));
}

/* The faint 5° tick grid — the calm stand-in for the old numeric labels. */
.arc-tick {
  stroke: rgba(156, 163, 175, 0.55);
  stroke-width: 1.2;
  stroke-linecap: round;
}

.dark .arc-tick {
  stroke: rgba(107, 114, 128, 0.6);
}

/* The reading tiles (only the values this device reports). */
.climate-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9.375rem, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* Every chip row (operation mode, fan steps, heat/cool). */
.climate-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* A labelled row (the fan speed row). */
.climate-row {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}

.climate-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75em;
  min-width: 0;
}

.climate-row-key {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  font-size: 0.8125rem;
  color: rgb(var(--gray-700));
}

.dark .climate-row-key {
  color: rgb(var(--gray-200));
}

.climate-row-value {
  font-size: 0.8125rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--gray-900));
}

.dark .climate-row-value {
  color: #fff;
}

/* The history panel keeps its place; only the container takes the divider. */
.climate-history {
  padding-top: calc(0.875rem * var(--ui-space-factor));
  border-top: 1px solid var(--glass-edge-soft);
}

.climate-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
}

.dark .climate-meta {
  color: rgb(var(--gray-400));
}

.climate-meta-left {
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
}

/* ── Card face ─────────────────────────────────────────────────────────────── */
/* The gauge is a small dial, so the media box is shorter than a picture's. */
.thermostat-card-face {
  --card-media: 13.5rem;
  /* The gauge is the card, so the frame gives it its edges back. */
  --card-pad: 0.3rem 0.3rem 0.35rem;
  gap: 0.15rem;
}

.thermostat-card-face__glyph {
  font-size: 2.4rem;
}

.thermostat-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #475569;
}

.thermostat-card-face__value span {
  font-size: 0.8em;
  font-weight: 600;
}

.thermostat-card-face__current {
  margin-left: 0.25rem;
  font-weight: 500;
  opacity: 0.7;
}

.dark .thermostat-card-face__value {
  color: #cbd5e1;
}

.thermo-card-face__gauge,
.climate-card-face__gauge {
  width: 100%;
  height: 100%;
}

.thermostat-card-face__controls {
  width: 100%;
  justify-content: space-between;
}

/* The sheet sizes its gauge 300 × 300; on a card it takes the media box instead.
   The height drives the square, so the ring never crops and the two numbers keep
   their place in the middle. */
.thermo-card-face__gauge,
.climate-card-face__gauge {
  width: auto;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  aspect-ratio: 1;
  margin: 0 auto;
  cursor: default;
  touch-action: auto;
}

.thermo-card-face__gauge .thermo-hero-value,
.climate-card-face__gauge .climate-hero-value {
  font-size: 1.5rem;
  color: rgb(var(--gray-800));
}

.thermo-card-face__gauge .thermo-hero-unit,
.climate-card-face__gauge .climate-hero-unit {
  font-size: 0.62em;
}

.thermo-card-face__gauge .thermo-hero-caption,
.climate-card-face__gauge .climate-hero-caption {
  margin-top: 0.15rem;
  font-size: 0.66rem;
  letter-spacing: 0.06em;
}

.dark .thermo-card-face__gauge .thermo-hero-value,
.dark .climate-card-face__gauge .climate-hero-value {
  color: rgb(var(--gray-100));
}

/* The arc only spans ~270°, so the square around it reads as padding: the drawing
   is scaled past the box and the box itself is thinner than the ring. */
.climate-card-face :deep(.device-card__media),
.thermo-card-face :deep(.device-card__media) {
  overflow: visible;
  padding-bottom: 0.4rem;
}

.climate-card-face__gauge .arc-gauge-svg,
.thermo-card-face__gauge .arc-gauge-svg {
  transform: scale(1.14);
  transform-origin: center;
}

/* The two step circles, under the gauge, a thumb wide. */
.thermostat-card-face__controls .thermostat-temp-button {
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  flex: none;
}

.thermostat-card-face__controls .thermostat-temp-button .material-symbols-outlined {
  font-size: 1.35rem;
}

/* Heating output, the reading the setpoint bar used to take. */
.thermostat-card-face__heating {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #ea580c;
}

.thermostat-card-face__heating .material-symbols-outlined {
  font-size: 0.95rem;
}

.dark .thermostat-card-face__heating {
  color: #fb923c;
}

/* The card's media follows the size toggle: the max card draws the full ring
   with both readings and the heating line; the min card is the ring and the name
   only, so it stays a small square instead of a tall empty card. */
.thermostat-card-face.device-card--mid,
.climate-card-face.device-card--mid {
  --card-media: 9rem;
}

.thermostat-card-face.device-card--min,
.climate-card-face.device-card--min {
  --card-media: 5.5rem;
}

.thermostat-card-face.device-card--min .thermo-hero-caption,
.thermostat-card-face.device-card--min .thermostat-card-face__heating,
.climate-card-face.device-card--min .thermostat-card-face__heating {
  display: none;
}

/* The setpoint is the card's one job: it stays at every size, and the climate
   card leaves the room reading to the sheet. */
.climate-card-face .climate-hero-caption {
  display: none;
}

.climate-card-face .climate-hero-value {
  font-size: 1.25rem;
}
</style>
